import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';

import type { ApiError } from '@/core/api';
import * as api from './api';
import type { CalendarEventInput } from './types';

export const calendarKeys = {
  all: ['calendar-events'] as const,
  lists: () => [...calendarKeys.all, 'list'] as const,
  list: (params: api.CalendarEventListParams) => [...calendarKeys.lists(), params] as const,
};

export function useCalendarEvents(params: api.CalendarEventListParams = {}) {
  return useQuery({
    queryKey: calendarKeys.list(params),
    queryFn: () => api.listCalendarEvents(params),
    placeholderData: keepPreviousData,
  });
}

export function useCreateCalendarEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CalendarEventInput) => api.createCalendarEvent(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: calendarKeys.lists() });
      toast.success('Event created');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useUpdateCalendarEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: Partial<CalendarEventInput> }) =>
      api.updateCalendarEvent(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: calendarKeys.lists() });
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useDeleteCalendarEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.deleteCalendarEvent(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: calendarKeys.lists() });
      toast.success('Event deleted');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}
